import { forwardRef, useCallback, useMemo, useRef, useEffect, useImperativeHandle } from 'react';
import { css } from '@emotion/react';
import type { SuggestionDataItem } from 'react-mentions';
import { MentionsInput, Mention } from 'react-mentions';

import { getInputStyles, getInputContainerStyles, tokens, utils } from '@hotjar/hotsauce';
import type { User, UserId } from '@hotjar/user';
import { ListUserCard } from './ListUserCard';

import type { IndexedMentionItem } from '../utils/content-utils';
import { createMentionMarkup, MENTION_TRIGGER } from '../utils/content-utils';
import { SuggestionsFooter } from './SuggestionsFooter';

const SUGGESTION_FOOTER_ID = Date.now().toString(32);
const SUGGESTION_FOOTER_DISPLAY = ' ';
const LINE_HEIGHT = tokens.getTextStyleProperties('body2').lineHeight;
const INPUT_VERTICAL_PADDING = tokens.spacer.x1;

const { ZINDEX } = utils;

interface TextareaProps {
  disabled?: boolean;
  inputPlaceholder: string;
  onChange: (value: string, mentions: Array<IndexedMentionItem>) => void;
  onFocus?: () => void;
  organizationName: string;
  users: Array<User>;
  value: string;
  autofocus?: boolean;
  // FIXME: This appears to be unused.
  // eslint-disable-next-line react/no-unused-prop-types
  className?: string;
  hasOrgInvitePermission: boolean;
}

const suggestionFooterMarkup = createMentionMarkup(SUGGESTION_FOOTER_ID, SUGGESTION_FOOTER_DISPLAY);

function removeFooterMentionMarkup(text: string) {
  return text.replace(suggestionFooterMarkup, '');
}

function isNotFooterMention(mention: SuggestionDataItem) {
  return mention.id !== SUGGESTION_FOOTER_ID;
}

function usersToSuggestions(users: Array<User>): Array<SuggestionDataItem> {
  return users.map(({ id, name }) => ({ id, display: name }));
}

function findUser(users: Array<User>, id: UserId) {
  return users.find((user) => user.id === id);
}

// FIXME: Please fix this. You may refer to CFE-266 for more details.
/* eslint-disable func-params-args/func-params */
const renderUserSuggestion =
  (
    mentionsEnabled: boolean,
    users: Array<User>,
    organizationName: string,
    hasOrgInvitePermission: boolean,
    // eslint-disable-next-line func-params-args/func-params
  ) =>
  (
    { id }: SuggestionDataItem,
    search: string,
    highlightedDisplay: React.ReactNode,
    index: number,
    focused: boolean,
  ) => {
    if (id === SUGGESTION_FOOTER_ID && mentionsEnabled) {
      return (
        <SuggestionsFooter
          organizationName={organizationName}
          hasOrgInvitePermission={hasOrgInvitePermission}
        />
      );
    }

    const user = findUser(users, Number(id));
    if (!user) {
      return null;
    }

    return (
      <ListUserCard avatar={user.avatar} name={user.name} email={user.email} focused={focused} />
    );
  };
/* eslint-enable func-params-args/func-params */

const textColor = tokens.color.text_default;

const mentionStyles = css`
  color: ${tokens.BRAND_COLORS.cobalt_800};
`;

const suggestionHeight = 48;

const inputStyles = css`
  background-color: ${tokens.color.ui_background};
  box-sizing: border-box;

  ${tokens.text('body2')}
  /* NOTE: this one doesn't need to be repeated here as it's already declared
  in the text token but to provide consistent values with other places using it
  it simply is */
  line-height: ${LINE_HEIGHT}px;

  &__highlighter,
  &__input {
    color: ${textColor};
    line-height: inherit;
    min-height: ${LINE_HEIGHT}px;
    max-height: calc(${LINE_HEIGHT * 10}px + ${INPUT_VERTICAL_PADDING} * 2);
    overflow: auto !important;

    // NOTE: this is to increase specificity of the selector and a slightly cleaner alternative to !important
    && {
      padding: ${INPUT_VERTICAL_PADDING} ${tokens.spacer.x2};
    }

    &,
    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }
  }

  &__highlighter {
    border: none !important;

    /* important to show the text color of the mention */
    z-index: 9;
    pointer-events: none;
  }

  &__input {
    ${getInputContainerStyles({ isDisabled: false, isInvalid: false, isReadonly: false })}
    ${getInputStyles({ inputSize: 's', isIconPresent: false, isInvalid: false, readOnly: false })}

    resize: none;
  }

  &__suggestions {
    ${tokens.elevation(1)};

    border-radius: ${tokens.radius.m};
    max-height: calc(${suggestionHeight * 3}px + ${tokens.spacer.x1});
    overflow-y: auto;
    // NOTE: important is required to override styles put on the element itself
    // by the mentions itself; z-index: 1 is far to low here
    z-index: ${ZINDEX.MODAL + 1} !important;
    max-width: 300px;
  }
`;

export const Textarea = forwardRef<HTMLTextAreaElement | undefined, TextareaProps>(
  (
    {
      autofocus = false,
      disabled = false,
      inputPlaceholder,
      onChange,
      onFocus,
      organizationName,
      users,
      value,
      hasOrgInvitePermission,
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>();
    const mentionsEnabled = users.length > 0;

    useImperativeHandle(ref, () => textareaRef.current);

    useEffect(() => {
      const { current } = textareaRef;

      if (autofocus && current) {
        current.focus();
      }
    }, [autofocus, ref]);

    const suggestions = useMemo(() => usersToSuggestions(users), [users]);

    const mentionData = useCallback(
      (query, callback) => {
        const filteredSuggestions = suggestions.filter(({ display }) =>
          // NOTE: this is quite a naive searching algorithm
          // we may wish to use some kind of fuzzy search in the future
          // or even better - query it from API
          display.toLowerCase().includes(query.toLowerCase()),
        );

        callback([
          ...filteredSuggestions,
          { id: SUGGESTION_FOOTER_ID, display: SUGGESTION_FOOTER_DISPLAY },
        ]);
      },
      [suggestions],
    );

    const handleInputChange = useCallback(
      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      (event, newValue: string, oldValue, mentions: Array<SuggestionDataItem>) => {
        onChange(
          // NOTE: footer may be selected as a mention with the keyboard
          // we definitely don't want this to be saved on the backend
          removeFooterMentionMarkup(newValue),
          mentions.filter(isNotFooterMention) as Array<IndexedMentionItem>,
        );
      },
      [onChange],
    );

    return (
      <MentionsInput
        allowSpaceInQuery
        css={inputStyles}
        disabled={disabled}
        inputRef={textareaRef as React.RefObject<HTMLTextAreaElement>}
        maxLength={500}
        onChange={handleInputChange}
        onFocus={onFocus}
        placeholder={inputPlaceholder}
        suggestionsPortalHost={document.body}
        value={value}
      >
        <Mention
          appendSpaceOnAdd
          css={mentionStyles}
          data={mentionData}
          displayTransform={(id, display) => MENTION_TRIGGER + display}
          markup={createMentionMarkup('__id__', '__display__')}
          renderSuggestion={renderUserSuggestion(
            mentionsEnabled,
            users,
            organizationName,
            hasOrgInvitePermission,
          )}
          trigger={MENTION_TRIGGER}
        />
      </MentionsInput>
    );
  },
);
